import React, { useState, useEffect } from "react";
import { Tag, Space, Modal, Form, Input, message, Switch, Tooltip } from "antd";
import Button from "../../../components/common/button";
import Table from "../../../components/common/table";
import View from "../../../assets/icons/view.svg";
import Trash from "../../../assets/icons/trash.svg";
import { getFaqs, createFaq, updateFaq, deleteFaq } from "../../../api/faq";
import colors from "../../../share/constants/constants";
import DeleteMemberModal from "../../../components/common/modalMember";

const { TextArea } = Input;

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingFaq, setEditingFaq] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState(null);

  // Fetch FAQs
  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const response = await getFaqs();
      console.log(response.data, 'response.data');
      if (response) {
        // Sort FAQs by order
        const sortedFaqs = [...response].sort((a, b) => a.order - b.order);
        setFaqs(sortedFaqs);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      message.error("Failed to fetch FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Handle form submission for create/update
  const handleSubmit = async (values) => {
    try {
      // Ensure isActive is set (default to true if not provided)
      const faqData = {
        ...values,
        isActive: values.isActive !== undefined ? values.isActive : true,
        order: values.order || 0
      };

      if (editingFaq) {
        // Update existing FAQ
        await updateFaq(editingFaq._id, faqData);
        message.success("FAQ updated successfully");
      } else {
        // Create new FAQ
        await createFaq(faqData);
        message.success("FAQ created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      setEditingFaq(null);
      fetchFaqs();
    } catch (error) {
      console.error("Error saving FAQ:", error);
      message.error("Failed to save FAQ");
    }
  };

  // Handle edit button click
  const handleEdit = (record) => {
    setEditingFaq(record);
    form.setFieldsValue({
      question: record.question,
      answer: record.answer,
      isActive: record.isActive,
      order: record.order || 0
    });
    setModalVisible(true);
  };

  // Show delete confirmation modal
  const showDeleteModal = (id) => {
    setSelectedFaqId(id);
    setIsDeleteModalOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteFaq(selectedFaqId);
      message.success("FAQ deleted successfully");
      setIsDeleteModalOpen(false);
      fetchFaqs();
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      message.error("Failed to delete FAQ");
    }
  };

  // Add new FAQ
  const handleAddNew = () => {
    setEditingFaq(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Table columns
  const columns = [
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: 80,
    },
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
      render: (text) => <div className="max-w-md">{text}</div>,
    },
    {
      title: "Answer",
      dataIndex: "answer",
      key: "answer",
      render: (text) => (
        <div className="max-w-md overflow-hidden text-ellipsis whitespace-nowrap">
          {text}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "success" : "error"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-2 w-[100px]">
          <Tooltip placement="top" title={"Edit"}>
            <div
              onClick={() => handleEdit(record)}
              className="w-4 m-auto grid justify-center h-4 pt-1 rounded-full bg-lightLavender cursor-pointer"
            >
              <img src={View} alt="Edit" />
            </div>
          </Tooltip>
          <Tooltip placement="top" title={"Delete"}>
            <div
              onClick={() => showDeleteModal(record._id)}
              className="flex items-center justify-center w-4 h-4 rounded-full bg-lightCrimson cursor-pointer"
            >
              <img src={Trash} alt="Delete" />
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="grid gap-2 w-full">
      <div className="flex justify-between">
        <div className="flex pt-3 pl-3">
          <div className="flex items-center">
            <span className="text-lg font-bold text-black ml-1">
              Frequently Asked Questions
            </span>
          </div>
        </div>
        <div className="mt-2 mr-2">
          <Button
            title="Add New FAQ"
            className="w-15.8 h-3.8 br"
            borderRadius={5}
            onClick={handleAddNew}
            color={colors.primaryPurple}
          />
        </div>
      </div>

      <div>
        <div>
          <div className="min-h-screen flex items-start justify-start pt-0">
            <Table columns={columns} data={faqs} loading={loading} />
          </div>
        </div>
      </div>

      <Modal
        title={editingFaq ? "Edit FAQ" : "Add New FAQ"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="pt-4"
        >
          <Form.Item
            name="question"
            label="Question"
            rules={[{ required: true, message: "Please enter the question" }]}
          >
            <Input placeholder="Enter question" className={'w-full h-5.4 !rounded-xl border-unset'}/>
          </Form.Item>

          <Form.Item
            name="answer"
            label="Answer"
            rules={[{ required: true, message: "Please enter the answer" }]}
          >
            <TextArea
              placeholder="Enter answer"
              autoSize={{ minRows: 4, maxRows: 8 }}
              className={'w-full h-5.4 !rounded-xl border-unset'}
            />
          </Form.Item>

          <Form.Item
            name="order"
            label="Display Order"
            rules={[{ required: true, message: "Please enter display order" }]}
            initialValue={0}
          >
            <Input min={0} placeholder="Enter display order" className={'w-full h-5.4 !rounded-xl border-unset'} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              defaultChecked
            />
          </Form.Item>

          <Form.Item className="flex justify-center mb-0">
              <Button
                title={editingFaq ? "Update" : "Save"}
                htmlType="submit"
                color={colors.primaryPurple}
                borderRadius={5}
                className="flex items-center w-full !rounded-xl"
              />
          </Form.Item>
        </Form>
      </Modal>

      {isDeleteModalOpen && (
        <DeleteMemberModal
          title="Delete FAQ"
          text="Are you sure you want to delete this FAQ?"
          onCancel={() => setIsDeleteModalOpen(false)}
          onOk={handleDelete}
        />
      )}
    </div>
  );
};

export default FAQ;
